import { useMemo, useState } from "react";
import { emptyClient } from "../../api/clients";
import { ORDER_STATUSES, formatINR, toAmount } from "../../api/orders";
import { Field, PanelMessage, Spinner, formatDate } from "./shared";
import { BackIcon, ChevronIcon } from "./icons";
import styles from "./Admin.module.css";

// Ids come back from the API as numbers but state may hold strings.
const sameId = (a, b) => String(a ?? "") === String(b ?? "");

/*
  Clients: the businesses Visible Online has worked with or is quoting for.
  A client can be created by hand or from an enquiry ("Save as client").
*/
export default function ClientsPanel({ clients, orders, onNewOrder, initialEdit = null }) {
  const [editing, setEditing] = useState(initialEdit); // null | "new" | client object
  const [selectedId, setSelectedId] = useState(null); // client open in the detail view
  const [search, setSearch] = useState("");

  const ordersByClient = useMemo(() => {
    const map = {};
    (orders.items || []).forEach((o) => {
      const key = String(o.clientId ?? "");
      (map[key] = map[key] || []).push(o);
    });
    return map;
  }, [orders.items]);

  if (clients.error && !clients.items) {
    return (
      <PanelMessage>
        {clients.error} <button className={styles.linkBtn} onClick={clients.load}>Retry</button>
      </PanelMessage>
    );
  }
  if (clients.items === null) return <PanelMessage><Spinner /></PanelMessage>;

  const ordersOf = (c) => ordersByClient[String(c.id)] || [];

  const removeClient = (c) => {
    if (!window.confirm(`Delete client "${c.name || c.businessName}"? Their orders are kept.`)) return;
    clients.remove(c.id);
    setSelectedId(null);
    setEditing(null);
  };

  const selected = selectedId != null ? clients.items.find((c) => sameId(c.id, selectedId)) : null;

  // Detail view: one client, their contact details and orders. Editing swaps in the form.
  if (selected) {
    return (
      <div className={styles.dash} key="detail">
        <button className={styles.backBtn} onClick={() => { setSelectedId(null); setEditing(null); }}>
          <BackIcon /> All clients
        </button>
        {editing && editing !== "new" ? (
          <ClientForm
            client={editing}
            onCancel={() => setEditing(null)}
            onSave={async (data) => {
              await clients.update(editing.id, data);
              setEditing(null);
            }}
          />
        ) : (
          <ClientDetail
            client={selected}
            orders={ordersOf(selected)}
            onEdit={() => setEditing(selected)}
            onDelete={() => removeClient(selected)}
          />
        )}
        {clients.error && <p className={styles.error}>{clients.error}</p>}
      </div>
    );
  }

  const q = search.trim().toLowerCase();
  const visible = clients.items.filter((c) =>
    !q || [c.name, c.businessName, c.phone, c.email].some((v) => String(v || "").toLowerCase().includes(q))
  );

  return (
    <div className={styles.dash} key="list">
      {editing && (
        <ClientForm
          client={editing === "new" ? emptyClient : editing}
          onCancel={() => setEditing(null)}
          onSave={async (data) => {
            if (editing === "new") await clients.add(data);
            else await clients.update(editing.id, data);
            setEditing(null);
          }}
        />
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search name, business, phone or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search clients"
        />
        <div className={styles.headActions}>
          <button className={styles.ghostBtn} onClick={clients.load}>Refresh</button>
          <button className={styles.solidBtn} onClick={() => setEditing("new")}>+ Add client</button>
        </div>
      </div>

      {clients.error && <p className={styles.error}>{clients.error}</p>}

      {visible.length === 0 ? (
        <PanelMessage>
          {clients.items.length === 0
            ? "No clients yet. Add one, or save an enquiry as a client."
            : "No clients match your search."}
        </PanelMessage>
      ) : (
        <div className={`${styles.tableWrap} ${styles.stackWrap}`}>
          <table className={`${styles.table} ${styles.stackTable}`}>
            <thead>
              <tr>
                <th>Added</th>
                <th>Client</th>
                <th>Contact</th>
                <th>Notes</th>
                <th>Orders</th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => {
                const open = () => setSelectedId(c.id);
                const stop = (e) => e.stopPropagation();
                return (
                  <tr
                    key={c.id}
                    className={styles.rowLink}
                    onClick={open}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); } }}
                    tabIndex={0}
                    role="link"
                    aria-label={`Open client ${c.name || c.businessName}`}
                  >
                    <td className={styles.cellMeta}>{formatDate(c.createdAt)}</td>
                    <td className={styles.cellTitle}>
                      <strong>{c.name || "—"}</strong>
                      {c.businessName && <div className={styles.dim}>{c.businessName}</div>}
                    </td>
                    <td className={`${styles.contactCell} ${styles.cellWide}`} onClick={stop} onKeyDown={stop}>
                      {c.phone && <a href={`tel:${c.phone.replace(/\s+/g, "")}`}>{c.phone}</a>}
                      {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
                      {c.address && <span className={styles.dim}>{c.address}</span>}
                    </td>
                    <td className={`${styles.msgCell} ${styles.cellWide}`} data-label="Notes">{c.notes}</td>
                    <td className={styles.cellInline} data-label="Orders">{ordersOf(c).length}</td>
                    <td className={styles.cellActions} onClick={stop} onKeyDown={stop}>
                      <div className={styles.rowActions}>
                        <button className={styles.ghostBtn} onClick={() => onNewOrder(c)}>New order</button>
                      </div>
                    </td>
                    <td className={styles.rowChevron} aria-hidden="true"><ChevronIcon /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ClientDetail({ client: c, orders, onEdit, onDelete }) {
  const statusLabel = (v) => ORDER_STATUSES.find((s) => s.value === v)?.label || v;
  const live = orders.filter((o) => o.status !== "cancelled");
  const billed = live.reduce((sum, o) => sum + toAmount(o.total), 0);
  const due = live.reduce((sum, o) => sum + toAmount(o.due), 0);
  const contact = [
    c.phone && { label: "Phone", value: <a href={`tel:${String(c.phone).replace(/\s+/g, "")}`}>{c.phone}</a> },
    c.whatsapp && {
      label: "WhatsApp",
      value: <a href={`https://wa.me/${String(c.whatsapp).replace(/\D/g, "")}`} target="_blank" rel="noreferrer">{c.whatsapp}</a>,
    },
    c.email && { label: "Email", value: <a href={`mailto:${c.email}`}>{c.email}</a> },
    c.address && { label: "Address", value: c.address },
  ].filter(Boolean);

  return (
    <section className={styles.card}>
      <div className={styles.detailHead}>
        <div>
          <span className={styles.badge}>Client</span>
          <h2 className={styles.detailTitle}>{c.name || c.businessName || "—"}</h2>
          <p className={styles.sub}>
            {c.name && c.businessName ? `${c.businessName} · ` : ""}Added {formatDate(c.createdAt)}
          </p>
        </div>
        <div className={styles.detailActions}>
          <button className={styles.ghostBtn} onClick={onEdit}>Edit client</button>
          <button className={styles.dangerBtn} onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className={styles.detailGrid}>
        <div className={styles.detailBlock}>
          <h3>Contact</h3>
          {contact.length > 0 ? (
            <dl className={`${styles.kv} ${styles.kvFlush}`}>
              {contact.map((it) => (
                <div key={it.label}><dt>{it.label}</dt><dd>{it.value}</dd></div>
              ))}
            </dl>
          ) : (
            <p className={styles.dim}>No contact details recorded.</p>
          )}
        </div>

        <div className={styles.detailBlock}>
          <h3>Summary</h3>
          <dl className={`${styles.kv} ${styles.kvFlush}`}>
            <div><dt>Orders</dt><dd><strong>{orders.length}</strong></dd></div>
            <div><dt>Total billed</dt><dd>{formatINR(billed)}</dd></div>
            <div>
              <dt>Balance due</dt>
              <dd>{due > 0 ? <span className={styles.dueOpen}>{formatINR(due)}</span> : <span className={styles.duePaid}>Paid</span>}</dd>
            </div>
          </dl>
        </div>
      </div>

      {c.notes && (
        <div className={styles.detailBlock}>
          <h3>Notes</h3>
          <p className={styles.detailNotes}>{c.notes}</p>
        </div>
      )}

      <div className={styles.detailBlock}>
        <h3>Orders</h3>
        {orders.length === 0 ? (
          <p className={styles.dim}>No orders yet. Use "New order" from the clients list to add one.</p>
        ) : (
          <div className={`${styles.tableWrap} ${styles.stackWrap}`}>
            <table className={`${styles.table} ${styles.stackTable}`}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Product / Service</th>
                  <th className={styles.amt}>Amount</th>
                  <th className={styles.amt}>Payment status</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className={o.status === "cancelled" ? styles.rowClosed : ""}>
                    <td className={styles.cellTitle}>
                      <strong>{o.orderNo}</strong>
                      <div className={styles.dim}>{formatDate(o.orderDate)}</div>
                    </td>
                    <td className={`${styles.itemsCell} ${styles.cellWide}`}>
                      <ul>
                        {(o.items || []).map((it, i) => (
                          <li key={i}>{it.name}</li>
                        ))}
                      </ul>
                    </td>
                    <td className={`${styles.amt} ${styles.amountCell} ${styles.cellAmount}`} data-label="Amount">
                      <strong>{formatINR(o.total)}</strong>
                    </td>
                    <td className={`${styles.amt} ${styles.cellDue}`} data-label="Payment">
                      {toAmount(o.due) > 0 ? (
                        <span className={styles.dueOpen}>{formatINR(o.due)}</span>
                      ) : (
                        <span className={styles.duePaid}>Paid</span>
                      )}
                    </td>
                    <td className={styles.cellStatus} data-label="Delivery">
                      <span className={`${styles.pill} ${styles[`order_${o.status}`] || ""}`}>{statusLabel(o.status)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

export function ClientForm({ client, onSave, onCancel, title }) {
  const [form, setForm] = useState({ ...emptyClient, ...client });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() && !form.businessName.trim()) {
      setError("Enter at least a client name or a business name.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || "Could not save the client.");
      setBusy(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>{title || (client.id ? "Edit client" : "New client")}</h2>
      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <Field label="Client name" value={form.name} onChange={set("name")} />
        <Field label="Business name" value={form.businessName} onChange={set("businessName")} />
        <Field label="Phone" value={form.phone} onChange={set("phone")} />
        <Field label="WhatsApp" value={form.whatsapp} onChange={set("whatsapp")} />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} />
        <Field label="Address" value={form.address} onChange={set("address")} />
      </fieldset>
      <fieldset className={styles.group} disabled={busy}>
        <Field label="Notes" value={form.notes} onChange={set("notes")} textarea hint="Requirements, what they asked for, anything useful for follow-up." />
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save client"}
        </button>
        <button className={styles.ghostBtn} type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
